import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNotifications } from "./Dashboard/hooks/useNotifications";
import { getPageNumbers } from "./Utility/pagination";

const departments = [
    { id: "Result", name: "परीक्षा परिणाम" },
    { id: "Admit", name: "प्रवेश पत्र" },
    { id: "Schedule", name: "परीक्षा समय सारिणी" },
    { id: "ExamForm", name: "परीक्षा आवेदन पत्र" },
    { id: "ReExamForm", name: "पुनर्मूल्यांकन आवेदन पत्र" },
    { id: "Migration", name: "माइग्रेशन प्रमाण-पत्र फार्म" },
];

const ITEMS_PER_PAGE = 8;

export default function Exam() {
    const [activeContentId, setActiveContentId] = useState(departments[0].id);
    const [showSidebar, setShowSidebar] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const { notifications, loading, handleNotificationClick } = useNotifications(activeContentId);

    useEffect(() => {
        const handleResize = () => setShowSidebar(window.innerWidth >= 768);
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => setCurrentPage(1), [activeContentId, notifications]);

    const handleItemClick = (deptId) => {
        setActiveContentId(deptId);
        if (window.innerWidth < 768) setShowSidebar(false);
    };

    const selected = departments.find((dept) => dept.id === activeContentId);

    const totalPages = Math.ceil(notifications.length / ITEMS_PER_PAGE);
    const pageNumbers = getPageNumbers(currentPage, totalPages);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const currentData = notifications.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    // Stylish colors
    const accent = "#77236b";
    const accentLight = "#f2e9f4";
    const highlight = "#fffbe7";

    return (
        <div className="d-flex vh-100 flex-column flex-md-row bg-light">
            {/* Mobile Menu */}
            <button
                className="btn d-md-none m-2 shadow text-white fw-bold"
                onClick={() => setShowSidebar(!showSidebar)}
                style={{ background: accent, borderColor: accent, fontSize: 18 }}
            >
                ☰ मेनू
            </button>

            {/* Sidebar */}
            <aside
                className={`p-3 shadow-lg d-flex flex-column ${showSidebar ? "d-block" : "d-none d-md-flex"} col-md-3`}
                style={{ background: accentLight, minHeight: "100vh" }}
            >
                <h5 className="text-center fw-bold mb-4" style={{ color: accent, letterSpacing: 1 }}>परीक्षा</h5>
                <div className="flex-grow-1">
                    {departments.map((dept) => {
                        const isActive = dept.id === activeContentId;
                        return (
                            <button
                                key={dept.id}
                                className={`w-100 mb-2 py-2 rounded border-0 text-start shadow-sm fw-medium ${isActive ? " text-white" : " text-dark"}`}
                                style={{
                                    background: isActive ? accent : "#fff",
                                    fontWeight: isActive ? 700 : 500,
                                    outline: isActive ? `2px solid ${accent}` : "none",
                                    transition: "background 0.2s",
                                    fontSize: 16
                                }}
                                tabIndex={0}
                                onClick={() => handleItemClick(dept.id)}
                                aria-current={isActive ? "page" : undefined}
                            >
                                {dept.name}
                            </button>
                        );
                    })}
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-grow-1 p-3 bg-white shadow col-md-9 overflow-auto" style={{ minHeight: "100vh" }}>
                <h4 className="mb-3 text-center fw-bold" style={{ color: accent }}>
                    {selected?.name || "कृपया कोई विकल्प चुनें।"}
                </h4>

                {loading ? (
                    <div className="text-center py-4">लोड हो रहा है...</div>
                ) : (
                    <>
                        <div className="table-responsive">
                            <table className="table table-bordered align-middle">
                                <thead style={{ background: accentLight }}>
                                    <tr>
                                        <th>शीर्षक</th>
                                        <th>डाउनलोड</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentData.length > 0 ? (
                                        currentData.map((item, index) => (
                                            <tr key={index} style={{ background: index % 2 === 0 ? "#fff" : highlight }}>
                                                <td>{item.title_Hindi}</td>
                                                <td>
                                                    <button
                                                        className="btn btn-sm"
                                                        style={{ background: accent, color: "#fff" }}
                                                        onClick={() => handleNotificationClick(item)}
                                                    >
                                                        डाउनलोड करें
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="2" className="text-center py-3">कोई सूचना उपलब्ध नहीं है</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages && (
                            <nav aria-label="Pagination">
                                <ul className="pagination justify-content-center mb-2 flex-wrap gap-1">
                                    {/* First */}
                                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(1)}
                                            aria-label="पहला पृष्ठ"
                                            disabled={currentPage === 1}
                                            style={{ minWidth: 40 }}
                                        >{"<<"}</button>
                                    </li>
                                    {/* Previous */}
                                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                                            disabled={currentPage === 1}
                                            aria-label="पिछला"
                                            style={{ minWidth: 40 }}
                                        >{"<"}</button>
                                    </li>
                                    {pageNumbers.map((page, i) =>
                                        page === "..." ? (
                                            <li key={i} className="page-item disabled">
                                                <span className="page-link">…</span>
                                            </li>
                                        ) : (
                                            <li key={i} className={`page-item ${page === currentPage ? "active" : ""}`}>
                                                <button
                                                    className="page-link"
                                                    onClick={() => setCurrentPage(page)}
                                                    style={{
                                                        minWidth: 40,
                                                        background: page === currentPage ? accent : undefined,
                                                        color: page === currentPage ? "#fff" : undefined
                                                    }}
                                                    aria-current={page === currentPage ? "page" : undefined}
                                                >{page}</button>
                                            </li>
                                        )
                                    )}
                                    {/* Next */}
                                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                                            disabled={currentPage === totalPages}
                                            aria-label="अगला"
                                            style={{ minWidth: 40 }}
                                        >{">"}</button>
                                    </li>
                                    {/* Last */}
                                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(totalPages)}
                                            aria-label="आखिरी पृष्ठ"
                                            disabled={currentPage === totalPages}
                                            style={{ minWidth: 40 }}
                                        >{">>"}</button>
                                    </li>
                                </ul>
                            </nav>
                        )}
                    </>
                )}
            </main>
        </div>
    );
}
